import { startAuthentication } from '@simplewebauthn/browser'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Button, Divider, Flex, Form, Typography } from 'antd'
import { KeyRound, Mail } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  authorizationClientAuthMethodsRetrieve,
  authorizationPasskeyAuthenticateStartCreate,
  authorizationPasskeyAuthenticateVerifyCreate,
  authorizationSocialAppleStartClientCreateMutation,
  authorizationSocialGoogleStartClientCreate,
  authorizationTokenClientCreate,
  mfaSendOtpCreate,
  usersLoginAdminCreateMutation,
  type AccountUiTokenObtainWritable,
  type AuthorizationSocialGoogleStartClientCreateData
} from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY, LOGOUT_ENFORCED_KEY } from 'src/constants'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  getDeviceToken,
  getSocialAuthRedirectOrigin,
  setLocalStorageToken,
  useInlineApiErrors
} from 'src/utils'
import { rememberConsentReturnPath } from 'src/utils/consentReturnPath'

import {
  AuthLink,
  AuthHeader,
  LoginFormItems,
  LoginQrPanel,
  SocialMethodButtons,
  schedulePasskeyPrompt,
  type AuthMethodButton
} from '../components'
import {
  AppleBrandIcon,
  FingerprintIcon,
  GoogleBrandIcon
} from '../components/SocialProviderIcons'
import { useQrLoginAvailability } from '../hooks'
import { useStyles } from '../styles'
import {
  asMfaRequired,
  asAuthenticatedTokens,
  getAuthUrl
} from '../utils/authResponseAdapters'
import type {
  AuthenticatedTokens,
  MfaRequiredResponse
} from '../utils/authResponseAdapters'

import { EmailOtpRequest } from './EmailOtpRequest'

const { Text } = Typography

type LoginFormValues = Pick<
  AccountUiTokenObtainWritable,
  'username' | 'password'
>
type SocialProviderId = 'google' | 'apple'
type SocialProviderButtonConfig = {
  id: SocialProviderId
  labelKey: 'auth.signInWithGoogle' | 'auth.signInWithApple'
  icon: ReactNode
  testId: string
  loading: boolean
  disabled: boolean
  onClick: () => void
}
type LoginLocationState = {
  username?: string
}

const isSocialProviderId = (method: string): method is SocialProviderId =>
  method === 'google' || method === 'apple'

export const Login = () => {
  const { message } = App.useApp()
  const { showApiErrors, errorAlert, showErrorMessages } = useInlineApiErrors()
  const { updateUserData } = useSessionContext()
  const { pathname, search, state } = useLocation()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [form] = Form.useForm<LoginFormValues>()
  const locationState = state as LoginLocationState | null

  useEffect(() => {
    rememberConsentReturnPath(new URLSearchParams(search).get('redirect'))
  }, [search])

  const initialUsername =
    typeof locationState?.username === 'string'
      ? locationState.username.trim()
      : ''
  const [loginEmail, setLoginEmail] = useState(initialUsername)
  const normalizedPathname =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  // Email + password is the default sign-in; the email one-time-code flow is
  // an opt-in route so a reload or shared link keeps the chosen method.
  const isEmailOtpRoute = normalizedPathname === paths.auth.loginEmail

  const { data: clientAuthMethods, isLoading: isLoadingAuthMethods } = useQuery(
    {
      queryKey: ['client-auth-methods'],
      queryFn: async () => {
        const { data } = await authorizationClientAuthMethodsRetrieve()

        return data
      },
      staleTime: 5 * 60 * 1000
    }
  )

  const setLoginInProgress = (value: boolean) => {
    if (value) {
      sessionStorage.setItem(LOGIN_IN_PROGRESS_KEY, '1')
    } else {
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
    }
  }

  useEffect(() => {
    if (!initialUsername || form.getFieldValue('username')) {
      return
    }

    form.setFieldValue('username', initialUsername)
  }, [form, initialUsername])

  // Surface errors handed off from a social/email callback (which has no form
  // to attach an Alert to): known provider codes arrive as URL params, generic
  // failures as `authErrors` in router state. Seed the inline alert once, then
  // strip the carrier so a refresh or back-nav doesn't resurface a stale error.
  const seededCallbackErrorRef = useRef(false)

  useEffect(() => {
    if (seededCallbackErrorRef.current) return

    const params = new URLSearchParams(window.location.search)
    const seeded: string[] = []

    if (params.get('google_error') === 'google_not_linked_to_org') {
      seeded.push(t('auth.googleNotLinkedToOrg'))
    }
    if (params.get('apple_error') === 'apple_not_linked') {
      seeded.push(t('auth.appleNotLinkedToOrg'))
    }

    const stateErrors = (location.state as { authErrors?: string[] } | null)
      ?.authErrors
    if (Array.isArray(stateErrors)) {
      seeded.push(...stateErrors.filter((msg) => typeof msg === 'string'))
    }

    if (!seeded.length) return

    seededCallbackErrorRef.current = true
    showErrorMessages(seeded)
    navigate(paths.auth.login, { replace: true, state: null })
  }, [location.state, navigate, showErrorMessages, t])

  const { mutateAsync: fetchUserAfterLogin, isPending: isPendingUser } =
    useMutation({
      ...usersLoginAdminCreateMutation(),
      onSuccess: (response) => {
        setLoginInProgress(false)
        updateUserData(response)
        message.success(t('login_success'))
        navigate(paths.home)
      },
      onError: (e) => {
        setLoginInProgress(false)
        showApiErrors(e)
      }
    })

  // The tokens are the session: once they are stored the member is signed in,
  // so the profile call that follows is a convenience, not a gate. It used to be
  // both — and losing that one round-trip (an iOS passkey sheet can suspend the
  // page long enough to drop it) left the member on the form still holding a
  // valid session, with nothing on screen to say so.
  const enterAppWithTokens = async (tokens: AuthenticatedTokens) => {
    setLocalStorageToken(tokens)
    try {
      await fetchUserAfterLogin({})
    } catch {
      setLoginInProgress(false)
      navigate(paths.home)
    }
  }

  const { mutateAsync: startGoogleLogin, isPending: isPendingGoogleStart } =
    useMutation({
      mutationFn: () => {
        const redirectOrigin = getSocialAuthRedirectOrigin()
        const currentOrigin = window.location.origin
        const payload: AuthorizationSocialGoogleStartClientCreateData['body'] =
          {
            redirect_uri: `${redirectOrigin}${paths.auth.googleClientCallback}`
          }
        if (currentOrigin !== redirectOrigin) {
          payload.return_origin = currentOrigin
        }
        return authorizationSocialGoogleStartClientCreate({
          body: payload
        }).then(({ data }) => data)
      },
      onSuccess: (response) => {
        const authUrl = getAuthUrl(response)
        if (!authUrl) {
          throw new Error('Missing Google auth URL')
        }
        window.location.href = authUrl
      },
      onError: (e) => {
        setLoginInProgress(false)
        showApiErrors(e, form)
      }
    })

  const { mutate: startAppleLogin, isPending: isPendingAppleStart } =
    useMutation({
      ...authorizationSocialAppleStartClientCreateMutation(),
      onSuccess: (response) => {
        const authUrl = getAuthUrl(response)
        if (!authUrl) {
          throw new Error('Missing Apple auth URL')
        }
        window.location.href = authUrl
      },
      onError: (e) => {
        setLoginInProgress(false)
        showApiErrors(e, form)
      }
    })

  const { mutate: sendOtp, isPending: isPendingSendOtp } = useMutation({
    mutationFn: async (mfa: MfaRequiredResponse) => {
      const { data } = await mfaSendOtpCreate({
        headers: { Authorization: `Token ${mfa.mfa_access_token}` },
        throwOnError: true
      })

      return { data, mfa }
    },
    onSuccess: ({ data, mfa }) => {
      setLoginInProgress(false)
      navigate(paths.auth.confirmMfaOtp, {
        state: {
          ...(data as Record<string, unknown>),
          __mfaAccessToken: mfa.mfa_access_token,
          __mfaSetupRequired: mfa.mfa_setup_required,
          __created_at: new Date().toISOString()
        }
      })
    },
    onError: (e) => {
      setLoginInProgress(false)
      showApiErrors(e, form)
    }
  })

  const { mutateAsync: loginWithPassword, isPending: isPendingToken } =
    useMutation({
      mutationFn: async (body: AccountUiTokenObtainWritable) => {
        const deviceToken = getDeviceToken()

        const { data, error } = await authorizationTokenClientCreate({
          body,
          headers: deviceToken ? { 'X-Device-Token': deviceToken } : undefined
        })

        if (error) throw error

        return data
      },
      onSuccess: async (response) => {
        const tokens = asAuthenticatedTokens(response)
        if (tokens) {
          schedulePasskeyPrompt()
          await enterAppWithTokens(tokens)
          return
        }

        const mfa = asMfaRequired(response)
        if (mfa) {
          schedulePasskeyPrompt()
          sendOtp(mfa)
          return
        }

        setLoginInProgress(false)
        showApiErrors(new Error('Missing login response'), form)
      },
      onError: (e) => {
        setLoginInProgress(false)
        showApiErrors(e, form)
      }
    })

  const isPasskeyEnabled = useMemo(
    () =>
      clientAuthMethods?.auth_methods?.some(
        (method) => (method.method as string) === 'passkey' && method.enabled
      ) ?? false,
    [clientAuthMethods]
  )

  const { mutateAsync: loginWithPasskey, isPending: isPendingPasskey } =
    useMutation({
      mutationFn: async () => {
        const { data: options } =
          await authorizationPasskeyAuthenticateStartCreate({
            body: { platform: 'web' }
          })
        const challengeId = (options as Record<string, unknown>)
          .challenge_id as string
        const credential = await startAuthentication({
          optionsJSON:
            options as unknown as PublicKeyCredentialRequestOptionsJSON
        })
        const { data: tokens } =
          await authorizationPasskeyAuthenticateVerifyCreate({
            body: {
              credential: credential as unknown as Record<string, unknown>,
              challenge_id: challengeId,
              platform: 'web'
            }
          })

        return tokens
      },
      onSuccess: async (response) => {
        const tokens = asAuthenticatedTokens(response)
        if (tokens) {
          await enterAppWithTokens(tokens)
          return
        }
        setLoginInProgress(false)
        showApiErrors(new Error('Missing login response'), form)
      },
      onError: (e: Error) => {
        setLoginInProgress(false)
        if (e.name === 'NotAllowedError') return
        showApiErrors(e, form)
      }
    })

  const handlePasskeySignIn = () => {
    sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
    setLoginInProgress(true)
    loginWithPasskey()
  }

  const onSubmit = async () => {
    const values = await form.validateFields()
    sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
    setLoginInProgress(true)
    const serviceToken = import.meta.env.VITE_APP_SERVICE_TOKEN as
      | string
      | undefined
    await loginWithPassword({
      ...values,
      ...(serviceToken ? { service_token: serviceToken } : {})
    })
  }

  const isPasswordPending = isPendingToken || isPendingUser || isPendingSendOtp
  const isAnyAuthPending =
    isPasswordPending ||
    isPendingGoogleStart ||
    isPendingAppleStart ||
    isPendingPasskey ||
    isLoadingAuthMethods
  const isPasswordEnabled =
    clientAuthMethods?.auth_methods?.some(
      (method) => method.method === 'password' && method.enabled
    ) ?? true
  const isEmailOtpEnabled = useMemo(
    () =>
      clientAuthMethods?.auth_methods?.some(
        // `email_otp` is not yet in the generated ClientAuthMethodMethodEnum;
        // compare as string until `npm run openapi` regenerates the types.
        (method) => (method.method as string) === 'email_otp' && method.enabled
      ) ?? false,
    [clientAuthMethods]
  )
  const signInMethod: 'password' | 'email_otp' = isEmailOtpRoute
    ? 'email_otp'
    : 'password'

  useEffect(() => {
    if (isLoadingAuthMethods) {
      return
    }

    if (isEmailOtpRoute && !isEmailOtpEnabled && isPasswordEnabled) {
      navigate(`${paths.auth.login}${search}`, { replace: true })
      return
    }

    if (!isEmailOtpRoute && !isPasswordEnabled && isEmailOtpEnabled) {
      navigate(`${paths.auth.loginEmail}${search}`, { replace: true })
    }
  }, [
    isEmailOtpEnabled,
    isEmailOtpRoute,
    isLoadingAuthMethods,
    isPasswordEnabled,
    navigate,
    search
  ])

  const handleNavigateToPassword = () => {
    form.setFieldValue('username', loginEmail)
    navigate(`${paths.auth.login}${search}`)
  }

  const handleGoogleSignIn = () => {
    sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
    startGoogleLogin()
  }

  const handleAppleSignIn = () => {
    sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
    const redirectOrigin = getSocialAuthRedirectOrigin()
    const currentOrigin = window.location.origin
    const body: { redirect_uri: string; return_origin?: string } = {
      redirect_uri: `${redirectOrigin}${paths.auth.appleClientCallback}`
    }
    if (currentOrigin !== redirectOrigin) {
      body.return_origin = currentOrigin
    }
    startAppleLogin({ body })
  }

  const providerConfig: Record<
    SocialProviderId,
    Omit<SocialProviderButtonConfig, 'id'>
  > = {
    google: {
      labelKey: 'auth.signInWithGoogle',
      icon: <GoogleBrandIcon />,
      testId: 'login-google',
      loading: isPendingGoogleStart,
      disabled: isAnyAuthPending && !isPendingGoogleStart,
      onClick: handleGoogleSignIn
    },
    apple: {
      labelKey: 'auth.signInWithApple',
      icon: <AppleBrandIcon />,
      testId: 'login-apple',
      loading: isPendingAppleStart,
      disabled: isAnyAuthPending && !isPendingAppleStart,
      onClick: handleAppleSignIn
    }
  }

  const enabledSocialMethods: SocialProviderId[] =
    clientAuthMethods?.auth_methods
      ?.map((method) =>
        method.enabled ? (method.method as string | null) : null
      )
      .filter(
        (method): method is SocialProviderId =>
          method !== null && isSocialProviderId(method)
      ) ?? []
  const socialProviderButtons: SocialProviderButtonConfig[] =
    enabledSocialMethods.map((method) => ({
      id: method,
      ...providerConfig[method]
    }))
  // QR login: on multi-method orgs it lives in the layout's right panel
  // (desktop); a QR-only org renders it here in the card so every screen size
  // has a way in.
  const { isQrEnabled, isQrOnly } = useQrLoginAvailability()
  const hasAnyLoginMethod =
    isPasswordEnabled || socialProviderButtons.length > 0 || isQrEnabled

  // Social providers + passkey unified into one list. 2+ methods render as a
  // compact icon row; a single method stays a labelled button.
  const socialMethods: AuthMethodButton[] = [
    ...socialProviderButtons.map((provider) => ({
      id: provider.id,
      label: t(provider.labelKey),
      shortLabel: provider.id === 'google' ? 'Google' : 'Apple',
      icon: provider.icon,
      loading: provider.loading,
      disabled: provider.disabled,
      onClick: provider.onClick,
      testId: provider.testId
    })),
    ...(isPasskeyEnabled
      ? [
          {
            id: 'passkey',
            label: t('auth.signInWithPasskey'),
            shortLabel: 'Passkey',
            icon: <FingerprintIcon size={18} />,
            loading: isPendingPasskey,
            disabled: isAnyAuthPending && !isPendingPasskey,
            onClick: handlePasskeySignIn,
            testId: 'login-passkey'
          }
        ]
      : [])
  ]
  const emailOtpAlternativeMethods: AuthMethodButton[] = [
    ...(isPasswordEnabled
      ? [
          {
            id: 'password',
            label: t('auth.emailOtp.usePasswordInstead'),
            shortLabel: t('authMethods.password'),
            icon: <KeyRound size={18} strokeWidth={1.5} />,
            loading: false,
            disabled: isAnyAuthPending,
            onClick: handleNavigateToPassword,
            testId: 'login-password'
          }
        ]
      : []),
    ...socialMethods
  ]
  const handleNavigateToEmailOtp = () => {
    const username =
      (form.getFieldValue('username') as string | undefined) ?? ''
    setLoginEmail(username)
    navigate(`${paths.auth.loginEmail}${search}`)
  }
  const passwordAlternativeMethods: AuthMethodButton[] = [
    ...(isEmailOtpEnabled
      ? [
          {
            id: 'email_otp',
            label: t('auth.emailOtp.useOtpInstead'),
            shortLabel: t('auth.emailOtp.shortLabel'),
            icon: <Mail size={18} strokeWidth={1.5} />,
            loading: false,
            disabled: isAnyAuthPending,
            onClick: handleNavigateToEmailOtp,
            testId: 'login-email-otp'
          }
        ]
      : []),
    ...socialMethods
  ]

  // QR-only org: the QR panel is the entire sign-in surface (no form to show),
  // so it renders in the card on every screen size.
  if (isQrOnly) {
    return (
      <Flex orientation="vertical" gap={24} className="w-full">
        <LoginQrPanel variant="full" />

        <Flex
          align="center"
          justify="center"
          gap={6}
          wrap="wrap"
          className={styles.authFooter}
        >
          <Text type="secondary">{t('do_not_have_account')}</Text>
          <AuthLink to={paths.auth.register}>{t('sign_up')}</AuthLink>
        </Flex>
      </Flex>
    )
  }

  const formContent = (
    <Flex orientation="vertical" gap={24} className="w-full">
      {errorAlert}

      {signInMethod === 'email_otp' &&
      (isEmailOtpEnabled || isLoadingAuthMethods) ? (
        <Flex orientation="vertical" gap={16} className="w-full">
          <AuthHeader
            title={t('auth.emailOtp.title')}
            subtitle={t('auth.emailOtp.subtitle')}
          />

          <Flex orientation="vertical" gap={16} className={styles.loginActions}>
            <EmailOtpRequest
              autoFocus
              username={loginEmail}
              onUsernameChange={setLoginEmail}
            />

            {emailOtpAlternativeMethods.length > 0 && (
              <Flex orientation="vertical" gap={8}>
                <Divider
                  plain
                  className={styles.loginDivider}
                  data-testid="login-social-divider"
                >
                  {t('auth.or')}
                </Divider>
                <SocialMethodButtons methods={emailOtpAlternativeMethods} />
              </Flex>
            )}

            <Flex
              align="center"
              justify="center"
              gap={6}
              wrap="wrap"
              className={styles.authFooter}
            >
              <Text type="secondary">{t('do_not_have_account')}</Text>
              <AuthLink to={paths.auth.register}>{t('sign_up')}</AuthLink>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Flex orientation="vertical" gap={16} className="w-full">
          <AuthHeader
            title={t('welcome_back')}
            subtitle={t('sign_in_your_account')}
          />

          <Form
            form={form}
            layout="vertical"
            scrollToFirstError
            requiredMark={false}
            noValidate
            id="login-form"
            onValuesChange={(changedValues: Partial<LoginFormValues>) => {
              if (typeof changedValues.username === 'string') {
                setLoginEmail(changedValues.username)
              }
            }}
          >
            <Flex orientation="vertical" gap={16}>
              {!hasAnyLoginMethod && (
                <Alert
                  type="error"
                  title={t('auth.noLoginMethodsEnabled')}
                  showIcon
                />
              )}

              {isPasswordEnabled ? (
                <LoginFormItems
                  autoFocus={loginEmail ? 'password' : 'username'}
                />
              ) : (
                <Alert
                  type="info"
                  title={t('auth.passwordSignInUnavailable')}
                  showIcon
                />
              )}

              <Flex
                orientation="vertical"
                gap={16}
                className={styles.loginActions}
              >
                {isPasswordEnabled && (
                  <Button
                    block
                    size="large"
                    onClick={onSubmit}
                    type="primary"
                    htmlType="submit"
                    loading={isPasswordPending || isLoadingAuthMethods}
                    disabled={isAnyAuthPending}
                    form="login-form"
                  >
                    {t('sign_in')}
                  </Button>
                )}

                {passwordAlternativeMethods.length > 0 && (
                  <Flex orientation="vertical" gap={8}>
                    <Divider
                      plain
                      className={styles.loginDivider}
                      data-testid="login-social-divider"
                    >
                      {t('auth.or')}
                    </Divider>

                    <SocialMethodButtons methods={passwordAlternativeMethods} />
                  </Flex>
                )}

                <Flex
                  align="center"
                  justify="center"
                  gap={6}
                  wrap="wrap"
                  className={styles.authFooter}
                >
                  <Text type="secondary">{t('do_not_have_account')}</Text>
                  <AuthLink to={paths.auth.register}>{t('sign_up')}</AuthLink>
                </Flex>
              </Flex>
            </Flex>
          </Form>
        </Flex>
      )}
    </Flex>
  )

  // QR sign-in sits beside the form (wrapping below it on narrow screens); a
  // QR-only org is handled above, so here QR is just an additional method.
  if (!isQrEnabled) {
    return formContent
  }

  return (
    <div className={styles.loginInlineAuth}>
      <div className={styles.loginInlinePrimary}>{formContent}</div>
      <div className={styles.loginInlineQr}>
        <LoginQrPanel variant="compact" />
      </div>
    </div>
  )
}
